import { Badge, type BadgeVariant } from '../ui';
import { turnLabelKey, type ThreadTurn } from '../../../lib/contact-rows';
import { useTranslation } from '../../../lib/i18n';

/**
 * Whose move it is, as a badge. The ONE place the three turns become a style and a string, because
 * three call sites render it — the contact row, the inbox row, and a thread — and a mapping written
 * out three times is a mapping that drifts. `ChannelChip` exists for the same reason.
 *
 * The turn itself comes from `contact-rows.ts` (`threadTurn` / `contactTurn`), which is shared 1:1
 * with the two native ports. Only this mapping is web's: each surface owns its own chrome, and the
 * natives colour their chips with their own tokens.
 *
 * Every style and every string here already shipped. `waiting-on-you`, `waiting-on-a-reply` and
 * `done` are existing `--ds-badge-*` triples; `contacts.row.replied` and `tasks.status.waiting` were
 * translated into all 37 locales and then rendered by nothing at all. So this adds no token, no key
 * and no translation pass — it wires up vocabulary the product already paid for.
 */
const VARIANTS: Record<ThreadTurn, BadgeVariant> = {
  needsYou: 'waiting-on-you',
  awaitingReply: 'waiting-on-a-reply',
  closed: 'done',
};

/** `className` is for POSITIONING only (the inbox row pins its badge to the top, Figma 18480:1081) —
 *  never for colour, which is the variant's job and the token layer's problem. */
export function TurnBadge({ turn, className }: { turn: ThreadTurn; className?: string }) {
  const { t } = useTranslation();
  return <Badge variant={VARIANTS[turn]} size="tiny" label={t(turnLabelKey(turn))} className={className} />;
}
